import { Link } from 'react-router-dom'
import Box, { BoxProps } from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import { useLayoutContext } from 'src/layouts/layoutContext'
import useResponsive from 'src/hooks/useResponsive'
import IconButton from '@mui/material/IconButton'
import UserIcon from '../../UserIcon'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useAccountSelect } from 'src/hooks/useAccount'

const MenuHeaderWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  display: 'flex',
  position: 'sticky',
  zIndex: 1,
  top: 0,
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingRight: theme.spacing(6),
  paddingLeft: theme.spacing(6),
  transition: 'padding .25s ease-in-out',
  minHeight: theme.mixins.toolbar.minHeight,
}))

const LinkStyled = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
})

const VerticalNavHeader = () => {
  const user = useAccountSelect.user()
  // the workspace name set at registration / in the backoffice; the subdomain only until GET /user answers
  const workspaceName = user?.partner?.name || user?.partnerSubdomain

  const { setNavHandle } = useLayoutContext()
  const { isMobile } = useResponsive()

  return (
    <MenuHeaderWrapper sx={{ backgroundColor: 'transparent' }}>
      <Stack direction="row" justifyContent="center">
        <LinkStyled to="/">
          <Stack direction="row" columnGap={2} alignItems="center">
            <svg
              width="31"
              height="36"
              viewBox="0 0 31 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M30.1307 5.59775C30.1314 5.61374 30.1314 5.62977 30.1307 5.64576C29.0159 8.22275 27.336 10.4972 25.2234 12.2895C25.1096 12.4065 24.9849 12.5118 24.8512 12.6038C24.8173 12.6387 24.7793 12.6387 24.7793 12.6736L24.3562 13.001L24.3266 13.0228C23.5542 11.8857 22.5278 10.9573 21.3342 10.316C20.1406 9.67475 18.8149 9.33952 17.4692 9.33866H17.1646C15.8568 9.38191 14.5771 9.74053 13.4278 10.3858C12.2784 11.0311 11.2915 11.9451 10.546 13.0546C9.80053 14.1641 9.31717 15.4384 9.13465 16.7753C8.95212 18.1122 9.07551 19.4746 9.49494 20.7535H9.46109C9.33841 20.8233 9.19035 20.915 9.03806 21.0328L7.67588 22.015C7.57435 22.0848 7.50666 22.1547 7.40513 22.2245L7.33745 22.2944C6.4394 23.0322 5.61977 23.8662 4.8923 24.7825C4.20233 25.5863 3.59814 26.4644 3.09016 27.4016C3.00978 27.5413 2.92941 27.6766 2.84903 27.7988L2.81519 27.8468C0.467779 24.1134 -0.458743 19.6161 0.213868 15.2202C0.886479 10.8243 3.11071 6.84032 6.45865 4.03472C9.80658 1.22912 14.0417 -0.199907 18.3493 0.0225606C22.6568 0.245028 26.7325 2.10328 29.7922 5.23981C29.9065 5.35766 30.0207 5.47552 30.1307 5.59775Z"
                fill="url(#paint0_linear_1218_11159)"
              />
              <path
                d="M29.9995 30.431C29.9614 30.3394 29.9106 30.239 29.8599 30.1342C28.6027 27.3299 26.6807 24.8978 24.2716 23.0627L24.2377 23.0365C23.4479 24.1724 22.4028 25.0936 21.192 25.7208C19.9812 26.3481 18.6409 26.6629 17.2863 26.638C15.9317 26.6131 14.6032 26.2493 13.4148 25.5779C12.2264 24.9065 11.2137 23.9475 10.4636 22.7833L10.4256 22.8052C10.3029 22.8881 9.97714 23.1151 9.52872 23.4687C7.38076 25.2172 5.65836 27.4594 4.49881 30.0164C4.4901 30.03 4.483 30.0446 4.47766 30.06C4.67226 30.2826 4.87532 30.4965 5.0826 30.7191C6.705 32.3934 8.63111 33.7215 10.751 34.6276C12.8708 35.5337 15.1429 36.0001 17.4374 36.0001C19.7319 36.0001 22.004 35.5337 24.1238 34.6276C26.2437 33.7215 28.1698 32.3934 29.7922 30.7191C29.8726 30.6362 29.9572 30.5489 30.0333 30.4616C30.0291 30.4616 30.0206 30.4485 29.9995 30.431Z"
                fill="url(#paint1_linear_1218_11159)"
              />
              <path
                d="M22.8505 20.0036C22.4559 21.2095 21.7038 22.2575 20.7 22.9999C19.6962 23.7423 18.4914 24.1416 17.2555 24.1416C16.0195 24.1416 14.8148 23.7423 13.811 22.9999C12.8072 22.2575 12.055 21.2095 11.6605 20.0036C13.2589 18.7198 15.2281 18.0225 17.2555 18.0225C19.2828 18.0225 21.252 18.7198 22.8505 20.0036Z"
                fill="url(#paint2_linear_1218_11159)"
              />
              <path
                d="M23.1782 18.0229C23.1732 18.6978 23.0572 19.3669 22.835 20.0019C21.2365 18.7181 19.2673 18.0208 17.2399 18.0208C15.2126 18.0208 13.2434 18.7181 11.6449 20.0019C11.4344 19.3649 11.33 18.6958 11.336 18.0229C11.285 16.4227 11.8387 14.8645 12.8806 13.6761C13.9953 12.5411 15.5006 11.9059 17.0683 11.9091C17.6607 11.8996 18.251 11.983 18.8189 12.1565L19.3681 12.3685C20.4838 12.8042 21.4451 13.5777 22.1252 14.5871C22.8054 15.5965 23.1725 16.7944 23.1782 18.0229Z"
                fill="url(#paint3_linear_1218_11159)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_1218_11159"
                  x1="-0.0360828"
                  y1="13.9002"
                  x2="30.1307"
                  y2="13.9002"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#0BD68D" />
                  <stop offset="1" stop-color="#11D1A3" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_1218_11159"
                  x1="4.47766"
                  y1="29.4009"
                  x2="30.0333"
                  y2="29.4009"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#228DFF" />
                  <stop offset="1" stop-color="#574EF9" />
                </linearGradient>
                <linearGradient
                  id="paint2_linear_1218_11159"
                  x1="11.6605"
                  y1="21.0992"
                  x2="22.8505"
                  y2="21.0992"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#0445DF" />
                  <stop offset="1" stop-color="#4242DB" />
                </linearGradient>
                <linearGradient
                  id="paint3_linear_1218_11159"
                  x1="11.1987"
                  y1="15.9732"
                  x2="23.0409"
                  y2="15.9732"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#228DFF" />
                  <stop offset="1" stop-color="#574EF9" />
                </linearGradient>
              </defs>
            </svg>
            <Typography
              color="var(--left-nav-item-color)"
              fontWeight={600}
              fontSize={14}
              className="opacity"
              noWrap
              maxWidth={170}
              title={workspaceName}
            >
              {workspaceName}
            </Typography>
          </Stack>
        </LinkStyled>
      </Stack>
      {isMobile && (
        <IconButton onClick={setNavHandle}>
          <UserIcon icon="material-symbols:close" />
        </IconButton>
      )}
    </MenuHeaderWrapper>
  )
}

export default VerticalNavHeader
